import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, X, Search,
  Building2, MapPin, PlaneTakeoff, BedDouble, Car, Utensils, Lightbulb, Calendar,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { localStrings } from "../../i18n/homeStrings";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import DatePicker from "../DatePicker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "own" is somebody's own car, which is the ordinary way to reach a client a
// few hours away and had no entry of its own: it was going under "other" with
// an explanation typed in the note beside it.
const TRANSPORT_VALUES = ["rental", "own", "uber", "client", "public", "other"];

// Trasferte brand color (same as page header globe icon + home tile)
const TRASFERTE_PURPLE = "#9B7FD4";

// Built here rather than in the markup: an escape sequence is decoded in a
// JavaScript string and printed character for character in JSX text.
const DASH = "\u2014";

function SectionLabel({ icon: Icon, htmlFor, hint, children }) {
  return (
    <Label htmlFor={htmlFor} className="flex items-center gap-2 text-slate-600">
      <Icon className="w-4 h-4 shrink-0" style={{ color: TRASFERTE_PURPLE }} />
      <span>{children}</span>
      {hint && <span className="text-xs font-normal text-slate-400">{hint}</span>}
    </Label>
  );
}

/**
 * Choosing the client, or creating it, or doing neither.
 *
 * A client groups trips to the same place; it is not a condition for recording
 * one. An audit at a site that is nobody's client is an ordinary trip, so the
 * list carries an entry for going without.
 *
 * The list is filtered here rather than on the server because client names are
 * encrypted with a random nonce per value: two identical names are two
 * different ciphertexts, so there is no prefix search to be had server-side.
 * The browser already holds every client for this country, since the same list
 * colours the map, so filtering it costs nothing.
 *
 * Creating happens in place. Sending somebody to another screen to make a
 * client, and back again to use it, is how a form gets abandoned halfway.
 */
function ClientPicker({ countryCode, clients, value, chosen, onChange, onCreated, t }) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newContact, setNewContact] = useState("");
  const [newNotes, setNewNotes] = useState("");

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients.slice(0, 8);
    return clients
      .filter((c) => `${c.name || ""} ${c.city || ""}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, clients]);

  const startCreating = () => {
    setNewName(query.trim());
    setNewCity("");
    setNewContact("");
    setNewNotes("");
    setCreating(true);
  };

  const saveClient = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/trasferte/clients`,
        {
          country_code: countryCode,
          name: newName.trim(),
          city: newCity.trim() || null,
          contact: newContact.trim() || null,
          notes: newNotes.trim() || null,
        },
        { withCredentials: true }
      );
      // The server hands back the existing record rather than making a second
      // one, which is the path taken by anybody who typed a name that was
      // already there in different capitalisation.
      toast.success(data.already_existed ? t("trasferte.clientExisted") : t("trasferte.clientCreated"));
      onCreated(data);
      onChange(data);
      setCreating(false);
      setQuery("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("trasferte.clientSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  // Decided, either way: the picker collapses to a line, because the rest of
  // the form is what still needs answering.
  if (chosen) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
        {value ? (
          <>
            <Building2 className="w-4 h-4 shrink-0" style={{ color: TRASFERTE_PURPLE }} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800 break-words">{value.name}</p>
              {value.city && <p className="text-xs text-slate-500 break-words">{value.city}</p>}
            </div>
          </>
        ) : (
          <>
            <MapPin className="w-4 h-4 shrink-0 text-slate-400" />
            <p className="text-sm text-slate-600 min-w-0 flex-1">{t("trasferte.noClient")}</p>
          </>
        )}
        <button
          type="button"
          onClick={() => { onChange(undefined); setQuery(""); }}
          data-testid="trip-client-change"
          className="text-xs text-indigo-600 hover:underline shrink-0"
        >
          {t("trasferte.changeClient")}
        </button>
      </div>
    );
  }

  if (creating) {
    return (
      <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="trip-client-create">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-slate-700">{t("trasferte.newClient")}</span>
          <button
            type="button"
            onClick={() => setCreating(false)}
            className="text-xs text-slate-400 hover:text-slate-600"
          >
            {t("common.cancel")}
          </button>
        </div>
        <Input
          autoFocus
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={t("trasferte.clientName")}
          data-testid="trip-client-new-name"
          className="border-slate-200"
        />
        <Input
          value={newCity}
          onChange={(e) => setNewCity(e.target.value)}
          placeholder={t("trasferte.city")}
          data-testid="trip-client-new-city"
          className="border-slate-200"
        />
        <Input
          value={newContact}
          onChange={(e) => setNewContact(e.target.value)}
          placeholder={t("trasferte.clientContactPlaceholder")}
          data-testid="trip-client-new-contact"
          className="border-slate-200"
        />
        <Textarea
          value={newNotes}
          onChange={(e) => setNewNotes(e.target.value)}
          rows={2}
          placeholder={t("trasferte.clientNotesPlaceholder")}
          data-testid="trip-client-new-notes"
          className="border-slate-200 resize-none"
        />
        <Button
          type="button"
          onClick={saveClient}
          disabled={saving || !newName.trim()}
          data-testid="trip-client-new-save"
          className="w-full text-white"
          style={{ backgroundColor: TRASFERTE_PURPLE }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("trasferte.createClient")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("trasferte.searchClient")}
          data-testid="trip-client-search"
          className="pl-9 border-slate-200"
        />
      </div>

      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto">
        {matches.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(c)}
            data-testid={`trip-client-option-${c.id}`}
            className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center gap-2"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-800 break-words">{c.name}</p>
              {c.city && <p className="text-xs text-slate-400 break-words">{c.city}</p>}
            </div>
            <span className="text-xs text-slate-400 shrink-0">
              {c.visits === 1 ? t("trasferte.visitsOne") : t("trasferte.visitsCount", { count: c.visits || 0 })}
            </span>
          </button>
        ))}

        <button
          type="button"
          onClick={startCreating}
          data-testid="trip-client-create-btn"
          className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center gap-2 text-sm text-emerald-700"
        >
          <Plus className="w-3.5 h-3.5 shrink-0" />
          <span className="break-words">
            {query.trim()
              ? `${t("trasferte.createClient")} "${query.trim()}"`
              : t("trasferte.newClient")}
          </span>
        </button>

        {/* Going without. A site visit with no client behind it is an ordinary
            trip, and this is where somebody says so. */}
        <button
          type="button"
          onClick={() => onChange(null)}
          data-testid="trip-client-none"
          className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-500"
        >
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span>{t("trasferte.noClient")}</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Recording a trip somebody has already taken, or correcting one.
 *
 * Both, through the same form: an edit is the same questions with the answers
 * already filled in, and a second dialog for it would be the same fields drawn
 * twice and corrected once. `trip` decides which - absent it creates, present
 * it edits and every field starts from what is stored.
 *
 * Two columns rather than one long strip. Every field here is optional and
 * short, so a single column made a form that had to be scrolled to be read at
 * all, and a form you cannot see the end of reads as longer than it is.
 *
 * The split is by kind of answer: the left column is the trip itself, facts
 * with one value each. The right is what somebody would tell a colleague going
 * next — lists and prose, which grow.
 *
 * The client is asked for first because it decides how the trip is filed, not
 * because it is required. Picking one that already exists means its address
 * and notes are not retyped; declining leaves a free-text place instead.
 */
export default function AddTripModal({
  open, onClose, countryCode, countryName,
  clients, initialClient, trip, onClientCreated, onSaved,
}) {
  const { t, language } = useLanguage();
  const s = localStrings(language);

  const editing = Boolean(trip);

  // Everything below starts from the trip when there is one. These run on
  // mount and the page only renders this component while it is open, so each
  // opening starts from the record as it stands rather than from whatever was
  // typed last time.
  //
  // undefined: not decided yet. null: deliberately no client. Object: chosen.
  // An edit is always decided - the stored trip either has a client or does
  // not - so it opens on the collapsed line rather than on the search list.
  const [client, setClient] = useState(
    editing ? (initialClient ?? null) : (initialClient ?? undefined)
  );
  const [place, setPlace] = useState(trip?.city || "");
  const [dateFrom, setDateFrom] = useState(trip?.visited_from || "");
  const [dateTo, setDateTo] = useState(trip?.visited_to || "");
  const [airport, setAirport] = useState(trip?.airport || "");
  const [hotels, setHotels] = useState(
    trip?.hotels?.length ? [...trip.hotels] : [""]
  );
  const [transportType, setTransportType] = useState(trip?.transport_type || "rental");
  const [transportNote, setTransportNote] = useState(trip?.transport_note || "");
  const [restaurants, setRestaurants] = useState(
    trip?.restaurants?.length
      ? trip.restaurants.map((r) => ({ name: r.name || "", note: r.note || "" }))
      : [{ name: "", note: "" }]
  );
  const [notes, setNotes] = useState(trip?.notes || "");
  const [saving, setSaving] = useState(false);

  const decided = client !== undefined;

  // Both are plain YYYY-MM-DD, so comparing the strings compares the dates.
  const datesOutOfOrder = Boolean(dateFrom && dateTo && dateTo < dateFrom);

  const handleClose = () => {
    if (saving) return;
    // No reset: the page unmounts this component when it closes, so the next
    // opening builds its state fresh from whatever it is given. Clearing the
    // fields here would also have wiped an edit back to blank rather than back
    // to the trip.
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (datesOutOfOrder) {
      toast.error(t("trasferte.dateOrder"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: client ? client.id : null,
        country_code: countryCode,
        city: client ? null : (place.trim() || null),
        visited_from: dateFrom || null,
        visited_to: dateTo || null,
        airport: airport.trim() || null,
        hotels: hotels.map((h) => h.trim()).filter(Boolean),
        transport_type: transportType,
        transport_note: transportNote.trim() || null,
        restaurants: restaurants
          .map((r) => ({ name: (r.name || "").trim(), note: (r.note || "").trim() || null }))
          .filter((r) => r.name),
        notes: notes.trim() || null,
      };
      // The same body either way. On the edit path every field is present, so
      // clearing one is a real change rather than an omission the server would
      // read as "leave it alone".
      const { data } = editing
        ? await axios.patch(`${API}/trasferte/${trip.id}`, payload, { withCredentials: true })
        : await axios.post(`${API}/trasferte`, payload, { withCredentials: true });
      onSaved(data, client || null, editing);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("trasferte.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-3xl p-0 gap-0 max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
            <DialogTitle className="text-xl font-bold flex items-center gap-2 flex-wrap">
              <img
                src={`https://flagcdn.com/${(countryCode || "").toLowerCase()}.svg`}
                alt=""
                className="w-7 h-5 object-cover rounded-sm border border-slate-200 shrink-0"
              />
              <span className="break-words">
                {editing ? t("trasferte.editTrip") : t("trasferte.addModalTitle")} {DASH} {countryName}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {/* The client, first and across the full width: it decides how the
                trip is filed. Optional, like everything else here. */}
            <div className="px-6 pt-5 space-y-4">
              <div className="space-y-2">
                <SectionLabel icon={Building2} hint={t("trasferte.clientOptional")}>
                  {t("trasferte.client")}
                </SectionLabel>
                <ClientPicker
                  countryCode={countryCode}
                  clients={clients}
                  value={client || null}
                  chosen={decided}
                  onChange={setClient}
                  onCreated={onClientCreated}
                  t={t}
                />
              </div>

              {/* Only without a client: with one, the place is the client's. */}
              {decided && !client && (
                <div className="space-y-2">
                  <SectionLabel icon={MapPin} htmlFor="trip-place">{t("trasferte.place")}</SectionLabel>
                  <Input
                    id="trip-place"
                    data-testid="trip-place-input"
                    value={place}
                    onChange={(e) => setPlace(e.target.value)}
                    placeholder={t("trasferte.placePlaceholder")}
                    className="border-slate-200"
                  />
                </div>
              )}
            </div>

            <div className="px-6 py-5 grid md:grid-cols-2 gap-x-6 gap-y-4">
              {/* --- Left: the trip itself. One value each. --------------- */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <SectionLabel icon={Calendar}>{t("trasferte.dates")}</SectionLabel>
                  {/* Two ends, because a trip lasts days. Leaving the second
                      empty says it was one day, which is the common case and
                      should not cost a second click. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <span className="text-xs text-slate-500">{t("trasferte.dateFrom")}</span>
                      <DatePicker
                        testId="trip-date-from"
                        value={dateFrom}
                        onChange={setDateFrom}
                        placeholder={s("date.choose")}
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-slate-500">{t("trasferte.dateTo")}</span>
                      <DatePicker
                        testId="trip-date-to"
                        value={dateTo}
                        onChange={setDateTo}
                        placeholder={s("date.choose")}
                      />
                    </div>
                  </div>
                  {datesOutOfOrder && (
                    <p className="text-xs text-red-600" data-testid="trip-date-order-error">
                      {t("trasferte.dateOrder")}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <SectionLabel icon={PlaneTakeoff} htmlFor="trip-airport">{t("trasferte.airport")}</SectionLabel>
                  <Input
                    id="trip-airport"
                    data-testid="trip-airport-input"
                    value={airport}
                    onChange={(e) => setAirport(e.target.value)}
                    placeholder={t("trasferte.airportPlaceholder")}
                    className="border-slate-200"
                  />
                </div>

                <div className="space-y-2">
                  <SectionLabel icon={Car}>{t("trasferte.transport.label")}</SectionLabel>
                  <Select value={transportType} onValueChange={setTransportType}>
                    <SelectTrigger data-testid="trip-transport-select" className="border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRANSPORT_VALUES.map((v) => (
                        <SelectItem key={v} value={v}>{t(`trasferte.transport.${v}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    data-testid="trip-transport-note-input"
                    value={transportNote}
                    onChange={(e) => setTransportNote(e.target.value)}
                    placeholder={t("trasferte.transportNotePlaceholder")}
                    className="border-slate-200"
                  />
                </div>
              </div>

              {/* --- Right: what you would tell the next person. Lists. ---- */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <SectionLabel icon={BedDouble}>{t("trasferte.hotels")}</SectionLabel>
                  <div className="space-y-2">
                    {hotels.map((h, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input
                          data-testid={`trip-hotel-input-${idx}`}
                          value={h}
                          onChange={(e) => {
                            const cp = [...hotels];
                            cp[idx] = e.target.value;
                            setHotels(cp);
                          }}
                          placeholder={t("trasferte.hotelPlaceholder")}
                          className="border-slate-200"
                        />
                        {hotels.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setHotels(hotels.filter((_, i) => i !== idx))}
                            className="text-slate-400 hover:text-red-600 shrink-0"
                            data-testid={`trip-hotel-remove-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setHotels([...hotels, ""])}
                      data-testid="trip-hotel-add"
                      className="border-slate-200 text-slate-600"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      {t("trasferte.addHotel")}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <SectionLabel icon={Utensils}>{t("trasferte.restaurants")}</SectionLabel>
                  <div className="space-y-2">
                    {restaurants.map((r, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input
                          data-testid={`trip-restaurant-name-${idx}`}
                          value={r.name}
                          onChange={(e) => {
                            const cp = [...restaurants];
                            cp[idx] = { ...cp[idx], name: e.target.value };
                            setRestaurants(cp);
                          }}
                          placeholder={t("trasferte.restaurantName")}
                          className="border-slate-200 flex-1 min-w-0"
                        />
                        <Input
                          data-testid={`trip-restaurant-note-${idx}`}
                          value={r.note}
                          onChange={(e) => {
                            const cp = [...restaurants];
                            cp[idx] = { ...cp[idx], note: e.target.value };
                            setRestaurants(cp);
                          }}
                          placeholder={t("trasferte.restaurantNote")}
                          className="border-slate-200 flex-1 min-w-0"
                        />
                        {restaurants.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setRestaurants(restaurants.filter((_, i) => i !== idx))}
                            className="text-slate-400 hover:text-red-600 shrink-0"
                            data-testid={`trip-restaurant-remove-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setRestaurants([...restaurants, { name: "", note: "" }])}
                      data-testid="trip-restaurant-add"
                      className="border-slate-200 text-slate-600"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      {t("trasferte.addRestaurant")}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <SectionLabel icon={Lightbulb} htmlFor="trip-notes">{t("trasferte.tips")}</SectionLabel>
                  <Textarea
                    id="trip-notes"
                    data-testid="trip-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder={t("trasferte.tipsPlaceholder")}
                    className="border-slate-200 resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={saving}
              className="border-slate-200"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              data-testid="trip-save-btn"
              disabled={saving || datesOutOfOrder}
              className="bg-indigo-700 hover:bg-indigo-600 text-white min-w-[7rem]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("trasferte.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { ClientPicker, TRANSPORT_VALUES };
